import Image from "next/image";
import Link from "next/link";
import mentorsData from "@/data/mentors.json";

export default function WorkshopCard({ workshop, priority = false }) {
  // Find mentor data for this workshop from mentors.json
  const programMentors = mentorsData.find(
    (item) =>
      item.slug === workshop.slug || item.slug === `workshops/${workshop.slug}`
  );

  // Extract mentor information
  const mentors = [];
  if (programMentors) {
    for (let i = 1; i <= 11; i++) {
      const nameKey = `mentor${i}name`;
      const imageKey = `mentor${i}image`;
      const postKey = `mentor${i}post`;

      if (
        programMentors[nameKey] &&
        programMentors[imageKey] &&
        programMentors[postKey]
      ) {
        mentors.push({
          name: programMentors[nameKey],
          image: programMentors[imageKey],
          position: programMentors[postKey],
        });
      }
    }
  }

  // If no mentors found in mentors.json, fall back to the workshop's own mentor data
  if (mentors.length === 0) {
    const getVisibleAndAdditionalMentors = () => {
      let visibleMentors = [];
      let additionalCount = 0;

      for (let i = 1; i <= 12; i++) {
        if (workshop[`mentor${i}name`] && workshop[`mentor${i}image`]) {
          if (i <= 2) {
            visibleMentors.push({
              name: workshop[`mentor${i}name`],
              image: workshop[`mentor${i}image`],
              position: workshop[`mentor${i}post`],
            });
          } else {
            additionalCount++;
          }
        }
      }

      if (visibleMentors.length === 0 && workshop.mentorImage) {
        visibleMentors.push({
          name: workshop.name,
          image: workshop.mentorImage,
          position: workshop.designation,
        });
      }

      return { visibleMentors, additionalCount };
    };

    const { visibleMentors, additionalCount: fallbackAdditionalCount } =
      getVisibleAndAdditionalMentors();
    mentors.push(...visibleMentors);
  }

  // Determine how many mentors to display based on total count
  let visibleCount = 1;
  if (mentors.length >= 5) {
    visibleCount = 3;
  } else if (mentors.length >= 3) {
    visibleCount = 2;
  }

  // Calculate additional mentors beyond visible count
  const additionalCount = Math.max(0, mentors.length - visibleCount);

  // Get visible mentors
  const visibleMentors = mentors.slice(0, visibleCount);

  // Generate company names text (for "Multiple mentors from X, Y")
  const generateCompanyText = () => {
    if (mentors.length === 0) {
      return workshop.mentorCompanies || workshop.designation || "Top Companies";
    }

    const companies = [];

    mentors.forEach((mentor) => {
      const position = mentor.position || "";
      let companyName = "";

      // Case 1: "Position, Company"
      if (position.includes(",")) {
        const commaIndex = position.lastIndexOf(",");
        companyName = position.substring(commaIndex + 1).trim();
        if (companyName.includes(" ")) {
          const words = companyName
            .split(" ")
            .filter(
              (word) =>
                word.length > 2 &&
                !["and", "the", "from", "for", "with"].includes(
                  word.toLowerCase()
                )
            );
          if (words.length > 0) {
            companyName = words[0];
          }
        }
      }
      // Case 2: "Position at Company"
      else if (position.toLowerCase().includes(" at ")) {
        const atIndex = position.toLowerCase().lastIndexOf(" at ");
        companyName = position.substring(atIndex + 4).trim();
        if (companyName.includes(" ")) {
          companyName = companyName.split(" ")[0];
        }
      }
      // Case 3: common known companies or fallback
      else {
        const positionWords = position.split(" ");
        for (let i = 0; i < positionWords.length; i++) {
          const word = positionWords[i];
          if (
            [
              "Google",
              "Facebook",
              "Meta",
              "Apple",
              "Microsoft",
              "Amazon",
              "LinkedIn",
              "Netflix",
              "Uber",
              "Airbnb",
              "Swiggy",
              "Zomato",
              "Ola",
              "Dunzo",
              "Myntra",
              "Flipkart",
              "Paytm",
              "Adobe",
              "Razorpay",
              "Walmart",
              "BCG",
              "McKinsey",
              "Blinkit",
              "Samur",
              "Performance",
            ].includes(word)
          ) {
            companyName = word;
            break;
          }
        }
        if (!companyName && positionWords.length > 0) {
          companyName = positionWords[positionWords.length - 1];
        }
      }

      if (companyName && !companies.includes(companyName)) {
        companies.push(companyName);
      }
    });

    if (companies.length === 0) {
      return workshop.designation || "Top Companies";
    } else if (companies.length === 1) {
      return companies[0];
    } else if (companies.length === 2) {
      return `${companies[0]}, ${companies[1]}`;
    } else {
      return `${companies[0]}, ${companies[1]} & more`;
    }
  };

  const companyNamesText = generateCompanyText();

  // For the "Multiple mentors from" text
  const formatMentorText = () => {
    if (mentors.length <= 1) {
      return "";
    }

    const companies = companyNamesText.split(", ");
    if (companies.length === 1) {
      return companies[0];
    }

    if (companyNamesText.includes("& more")) {
      return companyNamesText;
    }

    if (companies.length >= 2) {
      return `${companies[0]} and ${companies[1]}${
        companies.length > 2 ? " Mini" : ""
      }`;
    }

    return companyNamesText;
  };

  const formattedMentorText = formatMentorText();

  return (
    <Link
      href={`/workshops/${workshop.slug}`}
      className="block no-underline h-full"
    >
      <div className="bg-white rounded-3xl overflow-hidden border flex flex-col h-full">
        {/* === IMAGE AREA (fixed 180px tall) === */}
        <div className="relative w-full h-[180px] overflow-hidden">
          <Image
            src={workshop.mainImage}
            alt={workshop.heading}
            fill
            className="object-cover"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            priority={priority}
          />
        </div>

        {/* === CONTENT AREA === */}
        <div className="p-4 bg-white flex flex-col flex-grow">
          <h2 className="text-[#002726] text-2xl font-medium mb-4">
            {workshop.heading}
          </h2>

          {/* Workshop Duration */}
          <div className="flex items-center gap-2 mb-4">
            <Image
              src="https://d31bwppm8yl9g2.cloudfront.net/learner/website/gsdu.svg"
              width={18}
              height={18}
              className="w-[18px] h-[18px]"
              style={{ objectFit: "contain" }}
              alt="Duration"
            />
            <span className="text-[#002726] text-sm font-medium">
              {workshop.duration || "10+ Hours"}
            </span>
          </div>

          {/* Price (always displayed) */}
          <div className="flex items-center mb-6">
            <span className="text-[#002726] text-xl font-medium">
              {workshop.price || "₹ 899"}
            </span>
          </div>

          {/* This spacer pushes the mentor row to the very bottom */}
          <div className="flex-grow" />

          {/* Mentor Details (avatars are 40×40) */}
          <div className="flex items-start gap-3">
            <div className="flex items-center mt-1">
              <div className="flex relative" style={{ height: 40 }}>
                {visibleMentors.length > 0 && (
                  <div className="w-10 h-10 rounded-full overflow-hidden border border-black relative">
                    <Image
                      src={visibleMentors[0].image}
                      alt={visibleMentors[0].name}
                      fill
                      className="object-cover"
                      sizes="40px"
                    />
                  </div>
                )}

                {visibleMentors.length > 1 && (
                  <div className="w-10 h-10 rounded-full overflow-hidden border border-black relative -ml-4">
                    <Image
                      src={visibleMentors[1].image}
                      alt={visibleMentors[1].name}
                      fill
                      className="object-cover"
                      sizes="40px"
                    />
                  </div>
                )}

                {additionalCount > 0 && (
                  <div className="w-10 h-10 rounded-full bg-[#012726] text-white flex items-center justify-center font-medium text-sm z-20 border-2 border-white -ml-4">
                    +{additionalCount}
                  </div>
                )}
              </div>
            </div>

            <div>
              {mentors.length > 1 ? (
                <>
                  <p className="text-[#002726] text-base font-medium">
                    Multiple mentors from
                  </p>
                  <p className="text-[#002726] text-sm">
                    {formattedMentorText}
                  </p>
                </>
              ) : mentors.length === 1 ? (
                <>
                  <p className="text-[#002726] text-base font-medium">
                    {mentors[0].name}
                  </p>
                  <p className="text-[#002726] text-sm">
                    {mentors[0].position}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-[#002726] text-base font-medium">
                    {workshop.name || "Mentor name"}
                  </p>
                  <p className="text-[#002726] text-sm">
                    {workshop.designation || "Designation"}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}